// =============================================================================
// KLAVIYO NEWSLETTER — direct-to-Klaviyo-list newsletter submit (sections/newsletter.liquid).
//
// OPT-IN + NON-BREAKING. The newsletter section renders a `.ub-newsletter__form-wrap` host that
// carries `data-klaviyo-list` + `data-klaviyo-key` ONLY when the merchant fills BOTH the
// "Klaviyo list ID" and "Klaviyo public API key" section settings. When either is blank the host
// has no data attrs, this module does NOTHING, and the native Shopify `{% form 'customer' %}`
// submits exactly as before (creates a Shopify contact).
//
// When both attrs are present we intercept the form submit, prevent the native POST, and POST the
// email to Klaviyo's client-side subscriptions API — the public company_id goes in the query string,
// so no private/secret key is ever in the request. HTTP 202 = success.
//
// hCAPTCHA INTEROP: the site runs an invisible hCaptcha on all forms. hCaptcha intercepts the
// submit, completes a challenge, then fires HTMLFormElement.prototype.submit() directly (bypassing
// instance-level form.submit overrides and submit-event listeners). The module-level WeakSet +
// prototype patch below blocks that call for our form only, so the Klaviyo AJAX path is the sole
// submission route — no native POST, no redirect, no duplicate Shopify success message.
//
// Dependency-free; bundled into assets/global.autojs.js via entry/global.js -> objects/global/
// index.js. Idempotent; re-binds on shopify:section:load (the theme editor re-renders the section).
// =============================================================================

const HOST = '[data-klaviyo-list][data-klaviyo-key]';
const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const REVISION = '2024-10-15'; // recent stable Klaviyo API revision
const SUCCESS_FALLBACK = 'Thanks for subscribing!';
const ERROR_FALLBACK = 'Something went wrong. Please try again.';

// ---------------------------------------------------------------------------
// Prototype-level submit block. hCaptcha stores a reference to
// HTMLFormElement.prototype.submit at init time and calls it via
// .call(form) after the challenge completes — instance-level overrides
// (form.submit = () => {}) don't intercept that. Patching the prototype with
// a WeakSet guard blocks the call only for forms we explicitly register,
// leaving every other form on the page unaffected.
// ---------------------------------------------------------------------------
const _blockedForms = new WeakSet();
const _nativeSubmit = HTMLFormElement.prototype.submit;
HTMLFormElement.prototype.submit = function () {
  if (_blockedForms.has(this)) return;
  _nativeSubmit.call(this);
};

// JSON:API subscription payload for POST /client/subscriptions/.
function subscriptionBody(email, listId) {
  return {
    data: {
      type: 'subscription',
      attributes: {
        profile: {
          data: {
            type: 'profile',
            attributes: { email },
          },
        },
      },
      relationships: {
        list: { data: { type: 'list', id: listId } },
      },
    },
  };
}

// Reuse the theme's `.newsletter-form__message` styling; find-or-create one node inside the form so
// repeat submits update in place. The server-rendered success/error nodes only exist after a native
// POST round-trip, so on this client-side path we inject our own carrying the same classes.
function showMessage(form, host, type) {
  let msg = form.querySelector('[data-klaviyo-message]');
  if (!msg) {
    msg = document.createElement('p');
    msg.setAttribute('data-klaviyo-message', '');
    msg.className = 'newsletter-form__message form__message';
    msg.setAttribute('role', 'status');
    msg.setAttribute('aria-live', 'polite');
    form.appendChild(msg);
  }
  const success = type === 'success';
  msg.classList.toggle('newsletter-form__message--success', success);
  msg.textContent = success
    ? host.getAttribute('data-klaviyo-success') || SUCCESS_FALLBACK
    : ERROR_FALLBACK;
}

async function submitToKlaviyo(email, listId, companyId) {
  const res = await fetch(
    `${ENDPOINT}?company_id=${encodeURIComponent(companyId)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: REVISION,
      },
      body: JSON.stringify(subscriptionBody(email, listId)),
    }
  );
  return res.status === 202; // 202 Accepted = signup queued
}

function onSubmit(event, host, form) {
  event.preventDefault();
  const listId = host.getAttribute('data-klaviyo-list');
  const companyId = host.getAttribute('data-klaviyo-key');
  const emailInput = form.querySelector('input[type="email"]');
  const email = emailInput ? emailInput.value.trim() : '';
  if (!listId || !companyId || !email) {
    showMessage(form, host, 'error');
    return;
  }
  const submitBtn = form.querySelector('[type="submit"]');
  if (submitBtn) submitBtn.disabled = true;
  submitToKlaviyo(email, listId, companyId)
    .then((ok) => {
      if (ok) {
        showMessage(form, host, 'success');
        form.reset();
        if (submitBtn)
          setTimeout(() => {
            submitBtn.disabled = false;
          }, 2000);
      } else {
        showMessage(form, host, 'error');
        if (submitBtn) submitBtn.disabled = false;
      }
    })
    .catch(() => {
      showMessage(form, host, 'error');
      if (submitBtn) submitBtn.disabled = false;
    });
}

function bindHost(host) {
  if (host.dataset.klaviyoBound === 'true') return; // idempotent across re-inits
  const form = host.matches('form') ? host : host.querySelector('form');
  if (!form) return;
  host.dataset.klaviyoBound = 'true';

  // Register this form in the blocked set — the prototype patch above will
  // no-op any HTMLFormElement.prototype.submit.call(form) from hCaptcha.
  _blockedForms.add(form);

  // Belt-and-suspenders: also no-op the instance method for scripts that
  // call form.submit() directly (not via the prototype).
  form.submit = () => {};

  form.addEventListener('submit', (event) => onSubmit(event, host, form));

  // Intercept the submit button's click in capture phase so our preventDefault()
  // fires before any bubble-phase third-party click handler.
  const submitBtn = form.querySelector('[type="submit"]');
  if (submitBtn) {
    submitBtn.addEventListener(
      'click',
      (event) => onSubmit(event, host, form),
      {
        capture: true,
      }
    );
  }
}

function init(scope) {
  const context = scope && scope.querySelectorAll ? scope : document;
  context.querySelectorAll(HOST).forEach(bindHost);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// Theme editor re-renders the section wrapper; re-bind only within the reloaded section.
document.addEventListener('shopify:section:load', (event) =>
  init(event.target)
);

export default init;
